import styled from "styled-components";
import { zeeguuOrange, zeeguuDarkOrange } from "../components/colors";

const ArticlePreview = styled.div`
  position: relative;
  margin-bottom: 1em;
  margin-top: 2em;
  padding-left: 0.8em;
  padding-right: 0.8em;
  padding-bottom: 1em;
`;

// × in the card's top-right corner. Dismissal pattern (Twitter
// recommended, Instagram suggestions): small, muted, unmistakable. A
// generous 36px hit area via padding even though the visible glyph is
// small.
const HideButton = styled.button`
  position: absolute;
  top: 0.2em;
  right: 0.2em;
  width: 2.2em;
  height: 2.2em;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: 1.1em;
  cursor: pointer;
  padding: 0;
  z-index: 1;
  &:active { color: var(--text-primary); }
`;

// Bookmark/save toggle overlaid on the image (top-right). Subtle dark
// circle scrim keeps the icon legible against any photo. Sibling of
// the image's Link so clicks here don't trigger navigation.
const SaveIconButton = styled.button`
  position: absolute;
  top: 0.5em;
  right: 0.5em;
  width: 2.8em;
  height: 2.8em;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  border: none;
  border-radius: 50%;
  color: #fff;
  cursor: pointer;
  padding: 0;
  z-index: 2;
  &:active { background: rgba(0, 0, 0, 0.65); }
`;

// Hide/dismiss toggle overlaid on the image (bottom-right), mirroring the Save
// button at top-right. Used in preview/titles browsing modes where the card's
// top-right × would collide with the Save icon on stacked mobile layouts.
const HideIconButton = styled.button`
  position: absolute;
  bottom: 0.5em;
  right: 0.5em;
  width: 2.8em;
  height: 2.8em;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  border: none;
  border-radius: 50%;
  color: #fff;
  cursor: pointer;
  padding: 0;
  z-index: 2;
  &:active { background: rgba(0, 0, 0, 0.65); }
`;
/*
  The div contains the article preview contents
  and defines the size of the images relative to the
  user's screen size. Note this does not include the source/
  publishing time or topics.
*/

// The text half of a feed card: title, meta, summary. Pairing it with the
// image as ArticleContent's two children is what lets the image lead — stacked
// above the text on mobile, beside it on desktop — with one DOM order for both.
const ContentColumn = styled.div`
  min-width: 0;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;

  @media (max-width: 990px) {
    width: 100%;
  }
`;

const ArticleContent = styled.div`
  width: 100%;
  display: flex;
  color: inherit;
  font-weight: inherit;

  @media (max-width: 990px) {
    flex-direction: row;
    flex-wrap: wrap;
  }

  gap: 0.5em;

  img {
    /* No margin: the photo has to fill ImageWithOverlay exactly, because Save
       and the destination pill anchor to that box -- any inset here leaves
       them floating off the picture's edges. Spacing is the flex gap's job. */
    /* Side-by-side layout (desktop / tablet landscape): a fixed width +
       aspect-ratio gives every card an identical thumbnail, so wide and
       tall source photos no longer make the feed ragged. object-fit: cover
       crops each photo to fill the 3:2 box. */
    width: 13em;
    aspect-ratio: 3 / 2;
    border-radius: 1em;
    align-self: flex-start;
    object-fit: cover;

    @media (max-width: 990px) {
      align-content: center;
      justify-content: space-around;
      align-items: center;
      /* Fill the card width with a height cap so portrait photos
         don't dominate. width: 100% on the img stretches the parent
         ImageWithOverlay too, keeping the Open + Save overlays
         aligned to the corners of the visible image. aspect-ratio is
         reset to auto here so the stacked layout keeps letting the photo
         drive height up to the max-height cap (unchanged from before). */
      width: 100%;
      max-width: 100%;
      max-height: 13em;
      aspect-ratio: auto;
    }

    /* Tablet portrait (e.g. iPad): full-width photos get a taller crop
       so they don't read as a thin banner. min-width excludes phones,
       which keep the shorter 13em cap above. */
    @media (min-width: 600px) and (max-width: 990px) {
      max-height: 30em;
    }
  }

  @media (max-width: 990px) {
    align-content: center;
    justify-content: space-around;
    align-items: center;
  }
`;

//Invisible component that allows to open the redirection
//notification modal when the article's title is clicked
//used within the titleLink(article) function
const InvisibleTitleButton = styled.button`
  font: inherit;
  color: inherit;
  text-align: left;
  padding: 0;
  margin: 0;
  cursor: pointer;
  background: none;
  border: none;
`;
//previously the color was defined as black and font-weight was 400 but dark orange was
//displayed and font-weight 500 because the article's title inside the titleLink(article) function
//was wrapped in a link tag and inherited its color and font weight settings.
//Currently the article's title is no longer wrapped in a link, this is why styling was updated
const ReadProgress = styled.span`
  font-size: medium;
  font-weight: 500;
  color: grey;

  &.complete {
    color: green;
  }
`;
const TitleContainer = styled.div`
  display: flex;
  width: calc(100% - 40px);
  align-items: center;
  gap: 0.5rem;
  position: relative;
`;

// Preview browsing mode: the whole card body is one tap target that opens the
// interactive overlay. Nested controls (Save, meta links) stopPropagation so
// they don't trigger this. Keyboard-focusable, with a visible focus ring.
const PreviewCardClickable = styled.div`
  cursor: pointer;
  outline: none;
  border-radius: 0.5em;

  &:focus-visible {
    outline: 2px solid var(--badge-text);
    outline-offset: 2px;
  }
`;

const Title = styled.div`
  font-size: 1.4em;
  color: var(--text-primary);
  padding-right: 0.3em;
  font-weight: 600;
  display: block;
  width: 100%;

  &.opened a {
    color: grey !important;

    &.completed {
    }
  }

  &.opened {
    color: grey !important;
  }
`;

const UrlSourceContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 0.3em 0px;
`;

const UrlSource = styled.span`
  font-size: 0.8em;
  font-style: italic;
  font-weight: 500;
`;

const UnfinishedArticleContainer = styled.div`
  margin-top: 0.5em;
  display: flex;
  position: relative;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  align-content: center;
  gap: 0.5em;

  img {
    margin: 0.5em;
    margin-left: 0;
    max-width: 8em;
    max-height: 8em;
    border-radius: 1em;
    align-self: center;
    object-fit: cover;
    @media (max-width: 990px) {
      display: none;
    }
  }
`;

const UnfinishedArticleStats = styled.div`
  font-weight: 550;
  margin-top: 0.5rem;
  margin-bottom: -0.75rem;
`;

let Summary = styled.div`
  font-size: 1.3em;
  color: var(--text-primary);
  line-height: 1.5em;
  margin-top: 0.36em;
  /* A line-length cap, not a fixed width: the summary sits in the card's text
     column now, and a hard 40em overruns it -- which ClampedSummary's
     overflow: hidden then clips mid-word rather than wrapping. */
  width: auto;
  max-width: 40em;
  @media (max-width: 990px) {
    width: 100%;
  }
`;

// Wraps the article image so the "Open" overlay can be absolutely
// positioned over it. The container is inline-block so it shrinks to
// the image's actual rendered size.
const ImageWithOverlay = styled.div`
  position: relative;
  display: inline-block;
  line-height: 0;
  /* As a flex child of ArticleContent this box would stretch to the row's full
     height (align-items defaults to stretch), and the bottom-anchored pill
     would then hang below the photo. The img carried align-self before the
     wrapper existed; it belongs on the wrapper now. */
  align-self: flex-start;

  @media (max-width: 990px) {
    display: block;
    width: 100%;
  }
`;

// Image-less cards drop the photo region entirely (an empty box reads as
// "broken"). The Open overlay and Save toggle that used to live on the
// photo regroup into a single left-aligned action row under the summary.
const SummaryActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2em;
  margin-top: 0.6em;
  /* Fixed action size (the parent Summary is a large 1.3em); both children
     inherit this so Save and Open match instead of falling back to their
     button/anchor defaults. line-height: 1 hugs the text box to the glyphs
     so the flex-centered icons sit level with the text rather than high. */
  font-size: 0.95rem;
  line-height: 1;
`;

// Save toggle as a labelled inline action (icon + "Save"/"Saved"), sized
// to match the adjacent "Open" link rather than scrimmed over a photo.
const SaveActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3em;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--text-muted);
  font: inherit;
  font-weight: 500;
  &:active {
    color: var(--text-primary);
  }
`;

// The destination label: a pill rather than a band across the whole bottom
// edge, so it reads as an object sitting on the photo -- same dark disc and
// inset as Save, so the two are a pair -- instead of a strip fencing the image
// off under every headline.
const ImageOpenPill = styled.span`
  position: absolute;
  right: 0.5em;
  bottom: 0.5em;
  padding: 0.3em 0.7em;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  font-size: 0.8em;
  font-weight: 500;
  letter-spacing: 0.02em;
  line-height: 1.2;
  display: inline-flex;
  align-items: center;
  pointer-events: none;
`;

// Two-line clamp applied to the summary block when collapsed. Words
// still exist in the DOM (TranslatableText keeps per-word handlers
// alive) — only the visual is clipped.
const ClampedSummary = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

// Preview browsing mode teaser variants. The summary reads as "equally large"
// as the title (matches the 1.4em Title, vs the default 1.3em body) and is
// clamped to two lines so the teaser stays compact — the full interactive
// summary lives in the overlay.
const PreviewSummary = styled(Summary)`
  font-size: 1.4em;
`;

const PreviewClampedSummary = styled(ClampedSummary)`
  -webkit-line-clamp: 2;
`;

// What the article IS -- topic, AI provenance, saved -- as a kicker above
// the headline, the way news sites set the section over a title. Uppercase,
// letter-spaced and muted so it is read as a label, never as a second title
// or as a link: the old blue topic tag under the title pulled the eye away
// from the headline. Items are separated by "·" like MetaStrip, and the
// accent-coloured MetaTags that land here (class, Saved) are greyed too.
const Kicker = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 0.5em;
  margin-bottom: 0.3em;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-muted);
  line-height: 1.3;
  row-gap: 0.3em;

  > * {
    color: inherit;
    font-weight: inherit;
  }

  > * + *::before {
    content: "·";
    margin-right: 0.5em;
  }
`;

// A kicker topic that filters the feed. Reset to look exactly like the label
// it sits in -- quiet grey, not link blue -- with a taller hit area than the
// 0.72rem text so a thumb lands it.
const KickerTopic = styled.button`
  background: none;
  border: none;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  color: inherit;
  padding: 0.5em 0;
  margin: -0.5em 0;
  cursor: pointer;

  &:hover,
  &:active {
    color: var(--text-primary);
  }
`;

// The meta line and, at its end, the overflow that carries Hide. Hiding is
// rare and one-way, so it does not earn a permanent control next to Save --
// but it stays one tap away rather than buried in a menu elsewhere.
const MetaRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5em;

  > *:first-child {
    flex: 1 1 auto;
    min-width: 0;
  }
`;

const OverflowButton = styled.button`
  flex: 0 0 auto;
  background: none;
  border: none;
  padding: 0.1em 0.2em;
  margin: 0;
  cursor: pointer;
  color: var(--text-muted);
  display: inline-flex;
  align-items: center;
  border-radius: 0.3em;

  &:hover {
    color: var(--text-primary);
  }
`;

// Headlines (compact) card. Mobile: stacks image -> title -> meta -> actions.
// The photo leads because it says what the article is about before the reader
// has to parse a headline in a language they are still learning; it also keeps
// the image next to the headline it belongs to, instead of stranding it below
// the actions where it reads as the next card's photo.
// Desktop (>=991px): the same DOM reflows to a dense row, thumbnail on the LEFT
// and title + meta on the right — no `order` needed, the DOM is already in that
// sequence.
const CompactCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.7em;

  @media (min-width: 991px) {
    flex-direction: row;
    align-items: center;
    gap: 1em;

    &:hover ${SaveIconButton},
    ${SaveIconButton}:focus-visible {
      opacity: 1;
    }
  }
`;

const CompactText = styled.div`
  min-width: 0;

  @media (min-width: 991px) {
    flex: 1 1 auto;
  }
`;

const CompactMedia = styled.div`
  @media (min-width: 991px) {
    flex: 0 0 auto;

    /* The desktop thumbnail is a third the size of the phone's image: the
       Open band would cover a quarter of it and a resting Save circle
       another quarter. Drop the band, and let Save wait for the hover that
       only this surface has. */
    ${ImageOpenPill} {
      display: none;
    }

    ${SaveIconButton} {
      opacity: 0;
      transition: opacity 150ms ease;
    }
  }

  img {
    display: block;
    width: 100%;
    max-height: 13em;
    object-fit: cover;
    border-radius: 1em;

    @media (min-width: 991px) {
      width: 12em;
      height: 8em;
      max-height: none;
    }
  }
`;


// Bigger tap target than a bare "more" link: ~44px tall via padding.
// Negative left margin keeps the visible label flush with the summary
// left edge while the surrounding hit-area extends outwards. Accent
// color makes it read as a link, not as muted body text.
const SummaryToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25em;
  margin-top: 0.1em;
  margin-left: -0.6em;
  padding: 0.5em 0.6em;
  background: none;
  border: none;
  font: inherit;
  font-size: 0.85em;
  font-weight: 500;
  color: var(--badge-text);
  cursor: pointer;
`;

let BottomContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 0.5em;
  justify-content: space-between;
  @media (max-width: 990px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

let Topics = styled.span`
  span {
    height: 1.2em;
    margin-left: 0.2em;
    border: solid ${zeeguuOrange};
    border-radius: 2em;
    font-size: 0.85em;
    font-weight: 500;
    padding: 0.5em 1.35em;
    margin-bottom: 0.5em;
    text-align: center;
    vertical-align: middle;
  }
`;

export {
  Title,
  TitleContainer,
  ContentColumn,
  MetaRow,
  Kicker,
  KickerTopic,
  OverflowButton,
  ImageOpenPill,
  PreviewCardClickable,
  PreviewSummary,
  PreviewClampedSummary,
  CompactCard,
  CompactText,
  CompactMedia,
  UrlSource,
  UrlSourceContainer,
  ArticlePreview,
  UnfinishedArticleContainer,
  UnfinishedArticleStats,
  InvisibleTitleButton,
  ArticleContent,
  BottomContainer,
  Summary,
  ImageWithOverlay,
  SummaryActionRow,
  SaveActionButton,
  ClampedSummary,
  SummaryToggle,
  HideButton,
  SaveIconButton,
  HideIconButton,
  Topics,
  ReadProgress,
};
